import {
  RUNE_CATALOG,
  RUNE_PAGE_PRESETS,
  RUNE_SLOT_LIMITS
} from './runeCatalog.js';

const EMPTY_STATS = Object.freeze({
  ad: 0,
  ap: 0,
  armor: 0,
  mr: 0,
  armorPen: 0,
  msPercent: 0,
  goldPer10: 0,
  hpPerLevel: 0
});

export function createRunePage({
  id,
  name,
  marks = [],
  seals = [],
  glyphs = [],
  quintessences = []
} = {}) {
  return {
    id: id || 'custom',
    name: name || 'Custom',
    marks: [...marks],
    seals: [...seals],
    glyphs: [...glyphs],
    quintessences: [...quintessences]
  };
}

export function resolveRunePage(pageId) {
  const preset = RUNE_PAGE_PRESETS[pageId];
  if (!preset) return null;
  return createRunePage(preset);
}

function checkFamily(ids, expectedType, limit) {
  if (!Array.isArray(ids) || ids.length !== limit) {
    return { ok: false, reason: 'SLOT_COUNT' };
  }
  for (const id of ids) {
    const rune = RUNE_CATALOG[id];
    if (!rune || rune.type !== expectedType) {
      return { ok: false, reason: 'BAD_TYPE' };
    }
  }
  return { ok: true };
}

export function validateRunePage(page) {
  if (!page) return { ok: false, reason: 'MISSING' };
  const checks = [
    checkFamily(page.marks, 'mark', RUNE_SLOT_LIMITS.mark),
    checkFamily(page.seals, 'seal', RUNE_SLOT_LIMITS.seal),
    checkFamily(page.glyphs, 'glyph', RUNE_SLOT_LIMITS.glyph),
    checkFamily(page.quintessences, 'quintessence', RUNE_SLOT_LIMITS.quintessence)
  ];
  for (const result of checks) {
    if (!result.ok) return result;
  }
  return { ok: true };
}

function addRuneStats(acc, runeId) {
  const rune = RUNE_CATALOG[runeId];
  if (!rune || !rune.stats) return;
  for (const key of Object.keys(EMPTY_STATS)) {
    if (rune.stats[key]) acc[key] += rune.stats[key];
  }
}

export function aggregateRuneStats(page) {
  const acc = { ...EMPTY_STATS };
  if (!page) return acc;
  const families = [
    page.marks,
    page.seals,
    page.glyphs,
    page.quintessences
  ];
  for (const ids of families) {
    if (!Array.isArray(ids)) continue;
    for (const id of ids) addRuneStats(acc, id);
  }
  return acc;
}

/**
 * Applies rune page to entity. Stores `runeStats` + `goldPer10` for inventory /
 * passive gold. Mutates armor/mr/ap/armorPen once (inventory does not own those).
 * AD / MS% / scaling HP are applied via InventorySystem.recalculateStats when
 * present; otherwise applied here via `applyOwnedCombatStats`.
 */
export function applyRunePageToEntity(entity, page, { level, applyOwnedCombatStats = true } = {}) {
  if (!entity || !page) return { ok: false, reason: 'MISSING' };
  const validation = validateRunePage(page);
  if (!validation.ok) return validation;

  stripPreviousRuneFlats(entity);
  const stats = aggregateRuneStats(page);
  entity.runeStats = { ...stats };
  entity.goldPer10 = stats.goldPer10
    + ((entity.masteryStats && entity.masteryStats.goldPer10) || 0);
  entity.runePageId = page.id;

  entity.armor = (entity.armor || 0) + stats.armor;
  entity.mr = (entity.mr || 0) + stats.mr;
  entity.ap = (entity.ap || 0) + stats.ap;
  entity.armorPen = (entity.armorPen || 0) + stats.armorPen;

  if (applyOwnedCombatStats) {
    applyOwnedRuneCombatStats(entity, stats, level);
  }

  return { ok: true, stats };
}

function stripPreviousRuneFlats(entity) {
  const prev = entity.runeStats;
  if (!prev) return;
  entity.armor = (entity.armor || 0) - (prev.armor || 0);
  entity.mr = (entity.mr || 0) - (prev.mr || 0);
  entity.ap = (entity.ap || 0) - (prev.ap || 0);
  entity.armorPen = (entity.armorPen || 0) - (prev.armorPen || 0);
}

function applyOwnedRuneCombatStats(entity, stats, level) {
  const lvl = level ?? entity.level ?? 1;
  const hpFromScaling = stats.hpPerLevel * Math.max(0, lvl - 1);
  entity.ad = (entity.ad || 0) + stats.ad;
  if (stats.msPercent) {
    entity.ms = (entity.ms || 0) * (1 + stats.msPercent);
  }
  if (hpFromScaling) {
    entity.maxHp = (entity.maxHp || 0) + hpFromScaling;
    entity.hp = (entity.hp || 0) + hpFromScaling;
  }
}
